import { useState, useEffect } from "react";
import { useAdmin } from "../context/AdminContext";

const EVENT_TYPES = [
  // Session lifecycle
  "session_started", "session_ended", "session_forked", "agent_done",
  // Turn lifecycle
  "turn_start", "turn_end",
  // Messages
  "message_start", "message_end",
  // LLM
  "llm_call_start", "llm_call_end",
  // Tool execution (session-events)
  "tool_exec_start", "tool_exec_end", "tool_call", "tool_result", "tool_rejected", "tool_call_freq_exceeded",
  // Tool observability (NATS obs)
  "tool_start", "tool_end", "tool_error", "tool_timeout",
  "tool_args_replaced", "tool_pre_steered", "tool_post_steered",
  "tool_pre_escalated", "tool_post_escalated",
  // Hook observability
  "hook_fired", "hook_timeout", "hook_error",
  // HITL
  "hitl_req", "hitl_res",
  // Planning
  "plan", "step",
  // Context
  "context_updated", "context_ready",
  // Artifacts
  "artifact_updated", "artifact_invalidation",
  // Signals
  "signal_cancelled", "signal_interrupt", "signal_failed", "signal_start_failed",
  // Rate/quota
  "rate_limited", "quota_exhausted",
  // Harness
  "harness_ready", "harness_shutdown",
  // Misc
  "request_failed", "model_fallback", "model_circuit_breaker",
  "dlq_alert", "skill_execution", "snapshot", "tenant_context", "actor_spawn_failed",
] as const;

export function TracesPanel() {
  const { trace, account } = useAdmin();

  const [selectedAccountId, setSelectedAccountId] = useState<string>(() => {
    return account.accounts.entities[0]?.id ?? "";
  });

  useEffect(() => {
    if (!selectedAccountId && account.accounts.entities.length > 0) {
      setSelectedAccountId(account.accounts.entities[0].id);
    }
  }, [account.accounts.entities]);

  // Query state
  const [sessionId, setSessionId] = useState("");
  const [startSeq, setStartSeq] = useState(1);
  const [endSeq, setEndSeq] = useState(100);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [expandedSeq, setExpandedSeq] = useState<number | null>(null);

  // Load sessions on account change
  useEffect(() => {
    if (selectedAccountId) {
      trace.listSessions(selectedAccountId);
    }
  }, [selectedAccountId]);

  const handleQuery = async () => {
    if (!selectedAccountId || !sessionId) return;
    await trace.queryTraces(selectedAccountId, sessionId, startSeq, endSeq, includeDetails, eventTypeFilter || undefined);
  };

  const handleGetMaxSeq = async () => {
    if (!selectedAccountId || !sessionId) return;
    const max = await trace.getMaxSeq(selectedAccountId, sessionId);
    if (max > 0) {
      setEndSeq(max);
    }
  };

  const handleSelectSession = (sid: string) => {
    setSessionId(sid);
    // Auto-fetch max seq
    if (selectedAccountId) {
      trace.getMaxSeq(selectedAccountId, sid).then((max) => {
        if (max > 0) setEndSeq(max);
      });
    }
  };

  const handleDeleteSession = async (sid: string) => {
    if (!selectedAccountId) return;
    if (!confirm(`Delete all trace events for session ${sid}?`)) return;
    await trace.deleteSession(selectedAccountId, sid);
    if (sessionId === sid) {
      setSessionId("");
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!selectedAccountId) return;
    if (!confirm("Delete ALL trace events for this workspace?")) return;
    await trace.deleteWorkspace(selectedAccountId);
    setSessionId("");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Traces</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            View and manage session trace events
          </p>
        </div>
        <button
          onClick={handleDeleteWorkspace}
          disabled={!selectedAccountId}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
        >
          Delete All Workspace Traces
        </button>
      </div>

      {/* Account selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Account:</label>
        <select
          value={selectedAccountId}
          onChange={(e) => setSelectedAccountId(e.target.value)}
          className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="">Select account...</option>
          {account.accounts.entities.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      {/* Error banner */}
      {trace.error && (
        <div className="p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
          {trace.error}
        </div>
      )}

      {/* Sessions list */}
      {trace.sessions.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 font-medium text-sm text-gray-700 dark:text-gray-300">
            Sessions ({trace.sessions.length})
          </div>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 text-gray-600 dark:text-gray-400">Session ID</th>
                  <th className="text-right px-4 py-2 text-gray-600 dark:text-gray-400">Events</th>
                  <th className="text-left px-4 py-2 text-gray-600 dark:text-gray-400">First</th>
                  <th className="text-left px-4 py-2 text-gray-600 dark:text-gray-400">Last</th>
                  <th className="text-right px-4 py-2 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {trace.sessions.map((s) => (
                  <tr
                    key={s.sessionId}
                    className={`border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${sessionId === s.sessionId ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                    onClick={() => handleSelectSession(s.sessionId)}
                  >
                    <td className="px-4 py-2 font-mono text-xs">{s.sessionId}</td>
                    <td className="px-4 py-2 text-right">{s.eventCount}</td>
                    <td className="px-4 py-2 text-xs">{s.firstEventTime}</td>
                    <td className="px-4 py-2 text-xs">{s.lastEventTime}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.sessionId); }}
                        className="text-red-600 hover:text-red-800 text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Query controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-md">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Session ID</label>
          <input
            type="text"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Enter session ID..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm"
          />
        </div>
        <div className="w-24">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Start</label>
          <input
            type="number"
            value={startSeq}
            onChange={(e) => setStartSeq(Number(e.target.value))}
            min={1}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="w-24">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">End</label>
          <input
            type="number"
            value={endSeq}
            onChange={(e) => setEndSeq(Number(e.target.value))}
            min={1}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="w-48">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Event Type</label>
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          >
            <option value="">All types</option>
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeDetails}
            onChange={(e) => setIncludeDetails(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Details</span>
        </label>
        <button
          onClick={handleGetMaxSeq}
          disabled={!sessionId || !selectedAccountId}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Max Seq
        </button>
        <button
          onClick={handleQuery}
          disabled={!sessionId || !selectedAccountId || trace.loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {trace.loading ? "Loading..." : "Query"}
        </button>
      </div>

      {/* Max seq indicator */}
      {trace.maxSeq > 0 && sessionId && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Total events for session: <strong>{trace.maxSeq}</strong>
        </div>
      )}

      {/* Events table */}
      {trace.events.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-right px-3 py-2 text-gray-600 dark:text-gray-400 w-16">Seq</th>
                <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 w-40">Type</th>
                <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400 w-48">Time</th>
                <th className="text-left px-3 py-2 text-gray-600 dark:text-gray-400">Summary</th>
              </tr>
            </thead>
            <tbody>
              {trace.events.map((e) => (
                <>
                  <tr
                    key={e.seq}
                    className={`border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${expandedSeq === e.seq ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    onClick={() => setExpandedSeq(expandedSeq === e.seq ? null : e.seq)}
                  >
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-500">{e.seq}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        e.eventType.startsWith('tool_') ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        e.eventType.startsWith('hook_') ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        e.eventType.startsWith('hitl_') ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {e.eventType}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 font-mono">{e.eventTime}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{e.summary}</td>
                  </tr>
                  {expandedSeq === e.seq && (
                    <tr key={`${e.seq}-detail`} className="bg-gray-50 dark:bg-gray-800">
                      <td colSpan={4} className="px-3 py-3">
                        <div className="text-xs space-y-1">
                          <div><span className="font-medium text-gray-500">Subject:</span> <span className="font-mono">{e.subject}</span></div>
                          <div><span className="font-medium text-gray-500">Created:</span> {e.createdAt}</div>
                          {e.detail && (
                            <div>
                              <span className="font-medium text-gray-500">Detail:</span>
                              <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
                                {(() => {
                                  try { return JSON.stringify(JSON.parse(e.detail), null, 2); }
                                  catch { return e.detail; }
                                })()}
                              </pre>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!trace.loading && trace.events.length === 0 && sessionId && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No trace events found. Try adjusting the query range or filters.
        </div>
      )}
    </div>
  );
}
